import { useCallback } from 'react';
import type { ProfileType, Visibility } from '@yada/types';
import { setActiveProfile } from '@yada/storage';
import {
    createProfile as createProfileService,
    updateProfile as updateProfileService,
    deleteProfile as deleteProfileService,
    reorderProfiles as reorderProfilesService,
} from '../accounts/profileService';
import { useProfileContext } from '../context/ProfileContext';

export function getVisibilityForProfileType(profileType: ProfileType): Visibility {
    switch (profileType) {
        case 'internal': return 'private';
        case 'household': return 'workspace';
        case 'standard': return 'private';
        case 'pseudonymous': return 'public';
        case 'organization': return 'workspace';
        case 'bot': return 'private';
        default: return 'private';
    }
}

/**
 * Provides profile CRUD operations backed by the shared ProfileContext.
 * Components no longer need to manage their own profile state — they all
 * share the single ProfileContext fetch, which refreshes on every mutation.
 */
export function useProfiles() {
    const { activeProfile: currentProfile, profiles, isLoading, refreshProfiles } = useProfileContext();

    const switchProfile = useCallback(async (id: string) => {
        await setActiveProfile(id);
        window.dispatchEvent(new CustomEvent('yada:profile:changed', { detail: { profileId: id } }));
    }, []);

    const createProfile = useCallback(async (data: {
        name: string;
        profileType: ProfileType;
        metadata?: Record<string, unknown>;
    }) => {
        await createProfileService({ ...data, visibility: getVisibilityForProfileType(data.profileType) });
        // profileService already dispatches yada:profiles:updated → ProfileContext will refresh
    }, []);

    const updateProfile = useCallback(async (id: string, data: {
        name: string;
        profileType: ProfileType;
        metadata?: Record<string, unknown>;
    }) => {
        await updateProfileService(id, { ...data, visibility: getVisibilityForProfileType(data.profileType) });
        // profileService already dispatches yada:profiles:updated → ProfileContext will refresh
    }, []);

    const deleteProfile = useCallback(async (id: string) => {
        await deleteProfileService(id);
        // profileService already dispatches yada:profiles:updated → ProfileContext will refresh
    }, []);

    const reorderProfiles = useCallback(async (orderedIds: string[]) => {
        await reorderProfilesService(orderedIds);
    }, []);

    return {
        currentProfile,
        profiles,
        isLoading,
        refresh: refreshProfiles,
        switchProfile,
        createProfile,
        updateProfile,
        deleteProfile,
        reorderProfiles,
    };
}

